import {
  MANUAL_CONFIRM_TAG,
  ORDER_STAGE_HINTS,
  ORDER_STAGE_LABELS,
  RULE_MEANING_LABELS,
  canMarkConfirmed,
  canChangeStage,
  type Order,
  type WriteRejection,
} from "@estelle/domain";
import { Printer, TriangleAlert } from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { dateTime } from "@/modules/orders/format";
import { ChangeStageMenu } from "@/modules/orders/screens/ChangeStageMenu";
import { ViewJourneyLink } from "@/modules/orders/screens/OrderJourney";
import { RejectionNotice } from "@/modules/orders/screens/RejectionNotice";
import { ConfirmationLabel } from "@/modules/orders/screens/StageParts";
import { useOrderWrite } from "@/modules/orders/useOrderWrite";

/**
 * The stage's hint and its main action, under the order's title (#644): one
 * line saying what the stage means for this order ("COD order waiting for a
 * confirmation call."), and the button for what comes next where there is
 * one — New: Mark confirmed; Confirmed: Print packing slip. No new status is
 * invented; the words are `ORDER_STAGE_HINTS`, the same ones the legend uses.
 *
 * Mark confirmed adds `Manually Confirmed` through the order's own tag write,
 * so it is a write-through: Shopify accepts the tag first and the stage
 * changes from what Shopify returned. A refusal changes nothing and is shown
 * here with its reason.
 *
 * `actionsSlot` receives the buttons separately so a phone can pin them to the
 * bottom of the screen while the hint stays at the top.
 */
export function OrderStageHint({
  order,
  renderActions,
}: {
  order: Order;
  /** Where the buttons go; absent, beside the hint. */
  renderActions?: (actions: ReactNode) => ReactNode;
}) {
  const data = useDataSource();
  const permissions = usePermissions();
  const canEditTags = can(permissions, "orders.detail.editTags");
  const canOverrideStage = can(permissions, "orders.stage.override");
  const canPrint = can(permissions, "orders.list.export");
  const write = useOrderWrite(order.id);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);
  const [printNote, setPrintNote] = useState<string | null>(null);

  const isCod = order.paymentGatewayKey === "cod";
  const holdsManualTag = order.tags.some(
    (tag) => tag.toLowerCase() === MANUAL_CONFIRM_TAG.toLowerCase(),
  );

  const mutation = useMutation({
    mutationFn: (mode: "add" | "remove") =>
      mode === "add"
        ? data.orders.addTag(order.id, MANUAL_CONFIRM_TAG, {
            expectedUpdatedAt: order.channelUpdatedAt,
          })
        : data.orders.removeTag(order.id, MANUAL_CONFIRM_TAG, {
            expectedUpdatedAt: order.channelUpdatedAt,
          }),
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        write.accepted(result.data);
      } else {
        setRejection(result.rejection);
      }
    },
  });

  const print = useMutation({
    mutationFn: () => data.orders.packingSlips([order.id], "include"),
    onSuccess: (result) => {
      if (result.kind === "refused") {
        setPrintNote(result.message);
        return;
      }
      if (result.kind !== "pdf") return;
      setPrintNote(null);
      const url = URL.createObjectURL(
        new Blob([result.pdf], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `packing-slip-${order.orderNumber.replace(/\W/g, "")}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    },
  });

  const showMark = canEditTags && canMarkConfirmed(order.stage, isCod);
  const showUndo = canEditTags && isCod && holdsManualTag;
  const showPrint = canPrint && order.stage === "confirmed";

  const actions =
    showMark || showUndo || showPrint || canOverrideStage ? (
      <>
        {showMark ? (
          <Button
            size="sm"
            className="max-md:min-h-11 max-md:flex-1"
            disabled={mutation.isPending}
            onClick={() => {
              setRejection(null);
              mutation.mutate("add");
            }}
          >
            {mutation.isPending ? "Marking…" : "Mark confirmed"}
          </Button>
        ) : null}
        {showPrint ? (
          <Button
            size="sm"
            className="max-md:min-h-11 max-md:flex-1"
            disabled={print.isPending}
            onClick={() => {
              print.mutate();
            }}
          >
            <Printer />
            {print.isPending ? "Preparing…" : "Print packing slip"}
          </Button>
        ) : null}
        {showUndo ? (
          <Button
            size="sm"
            variant="outline"
            className="max-md:min-h-11"
            disabled={mutation.isPending}
            onClick={() => {
              setRejection(null);
              mutation.mutate("remove");
            }}
          >
            Undo confirmation
          </Button>
        ) : null}
        {canOverrideStage && canChangeStage(order) ? (
          <ChangeStageMenu
            orderIds={[order.id]}
            currentStage={order.stage}
            canClear={order.stageOverride !== null}
            onDone={() => {
              void write.reload();
            }}
          />
        ) : null}
      </>
    ) : null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <p className="min-w-0 text-sm">
          <span className="font-semibold">
            {ORDER_STAGE_LABELS[order.stage]}:
          </span>{" "}
          {ORDER_STAGE_HINTS[order.stage]}
          {order.overdueHours !== null ? (
            <span className="text-destructive font-medium">
              {" "}
              Overdue {order.overdueHours} hours.
            </span>
          ) : null}
        </p>
        {actions && !renderActions ? (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {actions}
          </div>
        ) : null}
      </div>
      {actions && renderActions ? renderActions(actions) : null}

      {order.stageOverride ? (
        <p className="text-muted-foreground text-sm">
          Set by hand by {order.stageOverride.setByName ?? "someone"} on{" "}
          {dateTime(order.stageOverride.setAt)}: {order.stageOverride.reason}.
          It holds until you change it or choose Automatic.
        </p>
      ) : null}

      {order.trackingStage ? (
        <div
          role="note"
          className="bg-tint-yellow flex gap-2 rounded-md p-2.5 text-sm"
        >
          <TriangleAlert
            aria-hidden
            className="mt-0.5 size-4 shrink-0 text-amber-700 dark:text-amber-300"
          />
          <p className="min-w-0">
            <span className="font-medium">
              Tracking says {ORDER_STAGE_LABELS[order.trackingStage]}.
            </span>{" "}
            The stage stays {ORDER_STAGE_LABELS[order.stage]} until it is
            changed or set to Automatic.
          </p>
        </div>
      ) : null}

      {rejection ? (
        <RejectionNotice
          rejection={rejection}
          onReload={() => {
            setRejection(null);
            void write.reload();
          }}
        />
      ) : null}
      {printNote ? (
        <p className="text-destructive text-sm">{printNote}</p>
      ) : null}
    </div>
  );
}

/**
 * The stage's story, for the Timeline tab (#644): how the confirmation call
 * went, and every stage the order has been in. The stage itself is said once,
 * in the header, not again here.
 */
export function OrderStageHistory({
  order,
  className,
}: {
  order: Order;
  className?: string;
}) {
  const data = useDataSource();
  const history = useQuery({
    queryKey: ["orders", "stage-history", order.id, order.stage],
    queryFn: () => data.stages.history(order.id),
  });
  const isCod = order.paymentGatewayKey === "cod";

  return (
    <section
      aria-label="Stage history"
      className={cn("flex flex-col gap-3 rounded-lg border p-4", className)}
    >
      {isCod ? (
        <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
          <dt className="text-muted-foreground">Confirmation call</dt>
          <dd>
            {order.confirmation ? (
              <ConfirmationLabel outcome={order.confirmation} />
            ) : (
              <span className="text-muted-foreground">No call recorded</span>
            )}
          </dd>
          {order.risk ? (
            <>
              <dt className="text-muted-foreground">Risk</dt>
              <dd>{RULE_MEANING_LABELS[`risk_${order.risk}`]}</dd>
            </>
          ) : null}
        </dl>
      ) : (
        <p className="text-muted-foreground text-sm">
          Prepaid, so it is confirmed without a call.
        </p>
      )}

      {history.data && history.data.length > 0 ? (
        <div>
          <div className="mb-1.5 flex items-center justify-between gap-3">
            <h3 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
              Stage history
            </h3>
            <ViewJourneyLink order={order} />
          </div>
          <ol className="flex flex-col gap-1 text-sm">
            {history.data.map((change) => (
              <li
                key={`${change.stage}-${change.enteredAt}`}
                className="flex flex-wrap items-baseline gap-x-3"
              >
                <span className="w-40 shrink-0">
                  {ORDER_STAGE_LABELS[change.stage]}
                </span>
                <span className="text-muted-foreground text-xs">
                  {dateTime(change.enteredAt)}
                </span>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </section>
  );
}
